import { window } from "global";
import { useEffect, useState } from "react";
import { MediaQuery } from "@outplayed/responsive";
import { TierListHeader } from "../../TierListHeader";
import { TierListPageNav } from "../../TierListPageNav";
import { TierListFilterManager } from "../../TierListFilterManager";
import { TierListTable } from "./TierListTable";
import { TierListVisualization } from "./TierListVisualization";
import { TierListTablePlaceholder } from "../../common/TierListTablePlaceholder";
import { TierListTableError } from "../../common/TierListTableError";

import { selectWinRate } from "@ugg/shared/api/data-parser/tier-lists/tier-list";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { useTierList } from "@ugg/shared/api/requests/tier-lists/tier-list";
import { useTierListPage, SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useUGGPatches } from "@ugg/shared/api/requests/patches";
import { TierListCTABanner } from "../../TierListCTABanner";
import { useRecurlySubscriptionStatus } from "@ugitgud/legos/hooks/usePremiumHooks";

type BannerProp = {
  bannerSlot?: JSX.Element | null;
  inNewWindow?: boolean;
  patchNotesCTA?: React.ReactNode;
};

export const TierList = ({ bannerSlot, inNewWindow, patchNotesCTA }: BannerProp) => {
  const [isTierListExpanded, setIsTierListExpanded] = useState(false);
  const queryParams = useQueryString();
  const page = useTierListPage();
  const { isActive: viewerIsPremium } = useRecurlySubscriptionStatus();
  const validateQueryParams = useValidateQueryParams(validParams);
  const { data, loading, error } = useTierList(page, { params: queryParams, ssr: true });

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  const validatedParams = validateQueryParams(page, queryParams, true);
  const filters = { ...validatedParams };
  const { role_win_rates, last_updated, total_matches_count } = selectWinRate(data, filters);

  const { data: patches } = useUGGPatches();
  const previousPatch = (() => {
    if (!patches || patches.length < 2) return null;
    if (filters.patch === "latest") return patches[1];
    const idx = patches.findIndex((p) => p === filters.patch);
    return idx >= 0 && idx + 1 < patches.length ? patches[idx + 1] : null;
  })();
  const { data: prevData } = useTierList(page, {
    params: { ...queryParams, patch: previousPatch ?? "latest" },
    skip: !previousPatch,
  });
  const { role_win_rates: prev_role_win_rates } = selectWinRate(prevData, filters);

  let table = null;
  if (!window) {
    table = null;
  } else if (loading) {
    table = <TierListTablePlaceholder />;
  } else if (error) {
    table = <TierListTableError />;
  } else {
    table = <TierListTable filters={filters} data={role_win_rates || []} />;
  }

  return (
    <div>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={total_matches_count}
        lastUpdated={last_updated}
        loading={loading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div className="mb-[24px]">
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot}
      {!viewerIsPremium && <TierListCTABanner className="mb-[24px]" inNewWindow={inNewWindow} />}
      <TierListFilterManager className="my-[24px]" />
      {role_win_rates &&
        role_win_rates.length > 0 &&
        !Object.values(SPECIAL_TIER_LIST_PAGES).includes(page as SPECIAL_TIER_LIST_PAGES) && (
          <TierListVisualization
            data={role_win_rates}
            previousData={prev_role_win_rates || undefined}
            filters={filters}
            className="mb-[24px]"
            isExpanded={isTierListExpanded}
            onExpandedChange={setIsTierListExpanded}
          />
        )}
      {table}
    </div>
  );
};
